import React, { useLayoutEffect, useRef, useState } from 'react';
import { Button, CircularProgress } from '@material-ui/core';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { previewProductCard } from '../model/queryRuleWorkbench';
import { ProductImage } from './MerchandisingWorkbenchUi';

export function SelectionSourceCount({ preview }) {
  const t = useLocalizedMessage();
  const status = preview?.status || 'validating';
  const response = preview?.response;
  return <span className={`mp-selection-source-count is-${status}`} aria-live="polite" aria-atomic="true">
    {status === 'ready'
      ? t(response.exact ? 'merchandising.selectionPreview.count' : 'merchandising.selectionPreview.estimatedCount', { count: response.total })
      : t(`merchandising.selectionPreview.${status === 'invalid' ? 'invalid' : status === 'error' ? 'error' : status === 'loading' ? 'loading' : 'validating'}`)}
  </span>;
}

// Keep the scroll range stable when fresh results replace cards with a status.
// Measure an inner block so the reserved space cannot feed back into its size.
function PreviewBody({ children }) {
  const naturalContent = useRef(null);
  const [space, setSpace] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const measure = () => {
      const bounds = naturalContent.current?.getBoundingClientRect();
      if (!bounds?.width || !bounds.height) return;
      setSpace(previous => {
        const height = previous.width === bounds.width
          ? Math.max(previous.height, bounds.height) : bounds.height;
        return previous.width === bounds.width && previous.height === height
          ? previous : { width: bounds.width, height };
      });
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(naturalContent.current);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [children]);
  return <div style={{ minHeight: space.height }}>
    <div className="mp-selection-source-preview-content" ref={naturalContent}>{children}</div>
  </div>;
}

export default function SelectionSourcePreview({ groupName, preview, onRetry }) {
  const t = useLocalizedMessage();
  const status = preview?.status || 'validating';
  const response = preview?.response;
  return <section className="mp-selection-source-preview" aria-label={t('merchandising.selectionPreview.productsLabel', { group: groupName })} aria-busy={status === 'loading' || status === 'validating'}>
    <div className="mp-selection-source-preview-heading">
      <strong>{t('merchandising.selectionPreview.title')}</strong>
      {status === 'ready' && <SelectionSourceCount preview={preview} />}
    </div>
    <PreviewBody>{status === 'error' ? <div className="mp-selection-source-preview-error" role="status">
      <span>{t('merchandising.selectionPreview.error')}</span>
      <Button variant="outlined" size="small" onClick={onRetry}>{t('merchandising.selectionPreview.retry')}</Button>
    </div> : status !== 'ready' ? <div className="mp-selection-source-preview-status" role="status">
      {status !== 'invalid' && <CircularProgress size={16} aria-hidden="true" />}
      {t(`merchandising.selectionPreview.${status === 'invalid' ? 'invalid' : status === 'loading' ? 'loading' : 'validating'}`)}
    </div> : response.total === 0 ? <p className="mp-selection-source-preview-status">{t('merchandising.selectionPreview.empty')}</p> : <>
      {response.total > response.products.length && <p className="mp-selection-source-preview-caption">{t('merchandising.selectionPreview.sample', { shown: response.products.length })}</p>}
      <div className="mp-selection-source-preview-products">
        {response.products.map(rawProduct => {
          const product = previewProductCard(rawProduct);
          return <article className="mp-selection-sample-card" key={product.ID}>
            <ProductImage product={product} />
            <div className="mp-selection-sample-card-body">
              <h4 title={product.PRODUCT_NAME}>{product.PRODUCT_NAME}</h4>
              <span>{product.SKU}</span>
              {product.PRICE && <strong>{product.PRICE}</strong>}
            </div>
          </article>;
        })}
      </div>
    </>}</PreviewBody>
  </section>;
}
